import { dataService } from '../../data';

import { DataObject, ObjectOptions, RelatedObject } from '../base';
import { CreateObject } from '../item';
import { Place } from './place';

import { TillView, TillViewProxy } from '../views/till';

import { QrCode } from './qrcode';
import { Rasppi } from './rasppi';

/****************************/
/* SCHEMA DEFINITION        */
/****************************/

import { PlaceType } from '../types';
import { RasppiType } from '../types';
import { TillChangeType } from '../types';

const _schema = {
    name: 'TILL',
    fields: [
        { name: 'status', type: 'string', default: "'AC'" },
        { name: 'name', type: 'string' },
        { name: 'place', type: 'objid' },
        { name: 'rasppi', type: 'objid' },
    ],
    relate: [
        { direction: '>', target: 'PLACE', by: 'place', name: 'place', reason: 'place', child: false, class: PlaceType },
        { direction: '>', target: 'RASPPI', by: 'rasppi', name: 'rasppi', reason: 'rasppi', child: false, class: RasppiType },
        { direction: '<', target: 'TILLCHANGE', by: 'till', name: 'tillchange', reason: 'tillchanges', child: true, class: TillChangeType },
    ]
} as const;

const _TillClass = CreateObject(_schema);
if (!_TillClass) {
    throw new Error("Failed to create BaseClass for '" + _schema.name + "'");
}

/****************************/
/* CLASS DECLARATION        */
/****************************/

export class Till extends _TillClass {
    constructor(objid: string | null, data: dataService, objoptions: ObjectOptions | null = null){
        super(_schema, objid, data, objoptions || {});
    }

    Copy(store: Array<DataObject> = []): Till {
        return this._Copy(store) as Till;
    }

    /** Changelog rows lack parent 1:N lists; register on link (see Preselect / TicketProduct). */
    protected override DoChildLinked(relation: string | null, related: RelatedObject | null): void {
        if (this.CopyOf || !this.Commited){
            return;
        }

        if (relation === 'place' && related instanceof Place){
            if (related === this.place){
                related.AddTill(this);
            }
            return;
        }

        if (relation === 'rasppi' && related instanceof Rasppi){
            if (related === this.rasppi){
                related.AddTill(this);
            }
        }
    }

    override get viewProxy(): TillView {
        return TillViewProxy(this, this.data);
    }

    /****************************/
    /* CUSTOM MEMBERS           */
    /****************************/

    get status(): string | null {
        return this['__base__status'];
    }

    /**
     * When status changes, notify the owning place:
     * - new till (not yet saved): remove from place.tills on DE
     * - persisted till: keep the row and refresh TillAreas cache on DE
     */
    set status(value: string){
        if (this.status == 'DE'){
            return;
        }

        if (this.status == value){
            return;
        }

        this['__base__status'] = value;

        const _place = this.place;
        if (!_place){
            return;
        }

        if ((this.status == 'DE') && !this.Commited){
            if (this.rasppi){
                this.rasppi.DelTill(this);
            }

            _place.DelTill(this);
        }
        else {
            _place.DoRefresh(this.table);
        }
    }

    get IsValid(): boolean {
        if (!this.status || this.status === 'DE'){
            return false;
        }

        if (!this.rasppi?.IsValid){
            return false;
        }

        return (this.rasppi.terminals?.length ?? 0) > 0;
    }

    /** Hardware service for this till; reads `TILL.rasppi` when the link is valid. */
    get service(): Rasppi | null {
        const _rasppi = this.rasppi;
        if (!_rasppi?.IsValid){
            return null;
        }

        return _rasppi;
    }

    set service(value: Rasppi | null){
        this.rasppi = value;
    }

    override get rasppi(): Rasppi | null {
        return super.rasppi;
    }

    override set rasppi(value: Rasppi | null){
        if (value){
            for (const _till of this.place?.tills ?? []){
                if ((_till === this) || !_till.status || _till.status === 'DE'){
                    continue;
                }

                if (_till.rasppi === value){
                    throw new Error("[TILL] RASPPI already linked to another active till");
                }
            }
        }

        super.rasppi = value;
    }

    /** Valid QRCODE rows assigned to this till (derived from `place.qrcodes` via `QRCODE.till`). */
    get qrcodes(): QrCode[] {
        const _place = this.place;
        if (!_place){
            return [];
        }

        const _qrcodes: QrCode[] = [];
        for (const _qrcode of _place.qrcodes ?? []){
            if (!_qrcode.IsValid){
                continue;
            }

            if (_qrcode.till === this){
                _qrcodes.push(_qrcode);
            }
        }

        return _qrcodes;
    }

    /** Whether this till's service is active and has at least one connected terminal. */
    get Connected(): boolean {
        return !!this.rasppi?.IsValid && this.rasppi.IsConnected;
    }
}
